import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, api, type Organization } from '../lib/api';
import { lmsApi, type GroupMemberView, type GroupView, type RosterMember } from '../lib/lmsApi';
import { Icon } from '../ui/icons';
import { formatDateShort } from './dates';

export interface GroupsViewProps {
  org: Organization;
  roster: RosterMember[];
  canManage: boolean;
  /** Student-facing copy and layout when the viewer is an ученик. */
  viewerIsStudent?: boolean;
  onOpenBoard: (boardId: string) => void;
}

/**
 * Tutoring groups: a named classroom with curators, optional teachers, and
 * students. Boards created here are visible to every member; only staff edit.
 *
 * Students open the same screen in a read-only form with three separate
 * blocks: lesson boards, their teacher(s), and classmates. The «Ваш учитель»
 * block is never shown to staff.
 */
export function GroupsView({
  org,
  roster,
  canManage,
  viewerIsStudent = false,
  onOpenBoard,
}: GroupsViewProps) {
  const [groups, setGroups] = useState<GroupView[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** A failed request said in words — otherwise it is an unhandled rejection and a silent screen. */
  const fail = useCallback((e: unknown, fallback: string) => {
    setError(e instanceof ApiError && e.code === 'unreachable' ? e.message : fallback);
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await lmsApi.listGroups(org.id);
      setGroups(list);
      setSelectedId((current) =>
        current && list.some((g) => g.id === current) ? current : (list[0]?.id ?? null),
      );
    } catch (e) {
      // Keeps whatever was on screen; an empty list would read as "групп нет".
      fail(e, 'Не удалось загрузить группы');
    } finally {
      setLoading(false);
    }
  }, [fail, org.id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const selected = groups.find((g) => g.id === selectedId) ?? null;

  const curators = useMemo(() => roster.filter((m) => m.kind === 'curator'), [roster]);
  const teachers = useMemo(() => roster.filter((m) => m.kind === 'teacher'), [roster]);
  const students = useMemo(() => roster.filter((m) => m.kind === 'student'), [roster]);

  const create = async () => {
    const name = draftName.trim() || 'Новая группа';
    setBusy(true);
    try {
      const group = await lmsApi.createGroup(org.id, name);
      setGroups((list) => [...list, group].sort((a, b) => a.name.localeCompare(b.name, 'ru')));
      setSelectedId(group.id);
      setDraftName('');
      setCreating(false);
      setError(null);
    } catch (e) {
      fail(e, 'Не удалось создать группу');
    } finally {
      setBusy(false);
    }
  };

  const patchSelected = async (
    patch: Parameters<typeof lmsApi.updateGroup>[1],
  ): Promise<void> => {
    if (!selected) return;
    setBusy(true);
    try {
      const updated = await lmsApi.updateGroup(selected.id, patch);
      setGroups((list) => list.map((g) => (g.id === updated.id ? updated : g)));
      setError(null);
    } catch (e) {
      fail(e, 'Не удалось сохранить группу');
    } finally {
      setBusy(false);
    }
  };

  const toggleMember = async (
    field: 'curatorIds' | 'teacherIds' | 'studentIds',
    userId: string,
  ) => {
    if (!selected) return;
    const current = new Set(selected[field]);
    if (current.has(userId)) current.delete(userId);
    else current.add(userId);
    await patchSelected({ [field]: [...current] });
  };

  const createBoard = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const board = await lmsApi.createGroupBoard(selected.id, `Занятие · ${selected.name}`);
      setGroups((list) =>
        list.map((g) =>
          g.id === selected.id
            ? {
                ...g,
                boards: [
                  {
                    id: board.id,
                    name: board.name,
                    updatedAt: board.updatedAt,
                    thumbnail: board.thumbnail,
                  },
                  ...g.boards,
                ],
              }
            : g,
        ),
      );
      onOpenBoard(board.id);
    } catch (e) {
      fail(e, 'Не удалось создать доску');
    } finally {
      setBusy(false);
    }
  };

  const renameBoard = async (boardId: string, name: string) => {
    const trimmed = name.trim() || 'Без названия';
    setBusy(true);
    try {
      const updated = await api.updateBoard(boardId, { name: trimmed });
      setGroups((list) =>
        list.map((g) => ({
          ...g,
          boards: g.boards.map((b) =>
            b.id === boardId ? { ...b, name: updated.name, updatedAt: updated.updatedAt } : b,
          ),
        })),
      );
      setError(null);
    } catch (e) {
      fail(e, 'Не удалось переименовать доску');
    } finally {
      setBusy(false);
    }
  };

  const removeGroup = async () => {
    if (!selected) return;
    if (
      !window.confirm(
        `Удалить группу «${selected.name}»? Доски останутся, но без привязки к группе.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await lmsApi.deleteGroup(selected.id);
      setGroups((list) => list.filter((g) => g.id !== selected.id));
      setSelectedId(null);
      setError(null);
    } catch (e) {
      fail(e, 'Не удалось удалить группу');
    } finally {
      setBusy(false);
    }
  };

  const studentMode = viewerIsStudent || selected?.viewerInGroupAs === 'student';

  return (
    <section className="dash-content groups-view">
      <header className="dash-header">
        <div>
          <h1>{studentMode ? 'Моя группа' : 'Группы'}</h1>
          <p className="dash-subtitle">
            {studentMode
              ? `${org.name} · одноклассники, ваш учитель и доски занятий`
              : `${org.name} · куратор ведёт группу, ученики видят её доски, правят только учитель и куратор`}
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            className="primary-btn"
            onClick={() => setCreating(true)}
            disabled={busy}
          >
            <Icon name="plus" size={14} />
            Новая группа
          </button>
        )}
      </header>

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}

      {creating && (
        <form
          className="group-create"
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          <input
            autoFocus
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            placeholder="Название группы, например «9А»"
          />
          <button type="submit" className="primary-btn" disabled={busy}>
            Создать
          </button>
          <button type="button" className="ghost-btn" onClick={() => setCreating(false)}>
            Отмена
          </button>
        </form>
      )}

      {loading ? (
        <p className="org-members-hint">Загрузка…</p>
      ) : groups.length === 0 ? (
        <div className="dash-empty">
          <Icon name="users" size={28} />
          <h2>{studentMode ? 'Вас ещё не добавили в группу' : 'Групп пока нет'}</h2>
          <p>
            {studentMode
              ? 'Когда куратор или учитель включит вас в группу, здесь появятся одноклассники, учитель и доски занятий.'
              : 'Создайте группу, назначьте куратора и добавьте учеников — затем откройте общую доску.'}
          </p>
        </div>
      ) : (
        <div className={`groups-layout ${studentMode && groups.length === 1 ? 'is-single' : ''}`}>
          {!(studentMode && groups.length === 1) && (
            <ul className="group-list">
              {groups.map((group) => (
                <li key={group.id}>
                  <button
                    type="button"
                    className={`group-list-item ${group.id === selectedId ? 'is-active' : ''}`}
                    onClick={() => setSelectedId(group.id)}
                  >
                    <strong>{group.name}</strong>
                    <span>
                      {group.studentIds.length} уч. · {group.boards.length}{' '}
                      {group.boards.length === 1 ? 'доска' : 'досок'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {selected && (
            <div className="group-detail">
              <header className="group-detail-head">
                {selected.canManage ? (
                  <input
                    className="group-name-input"
                    value={selected.name}
                    disabled={busy}
                    onChange={(e) =>
                      setGroups((list) =>
                        list.map((g) => (g.id === selected.id ? { ...g, name: e.target.value } : g)),
                      )
                    }
                    onFocus={(e) => {
                      e.currentTarget.dataset.savedName = selected.name;
                    }}
                    onBlur={(e) => {
                      const name = e.target.value.trim();
                      const saved = e.currentTarget.dataset.savedName ?? selected.name;
                      if (!name) {
                        setGroups((list) =>
                          list.map((g) => (g.id === selected.id ? { ...g, name: saved } : g)),
                        );
                        return;
                      }
                      if (name === saved.trim()) {
                        if (name !== e.target.value) {
                          setGroups((list) =>
                            list.map((g) => (g.id === selected.id ? { ...g, name } : g)),
                          );
                        }
                        return;
                      }
                      void patchSelected({ name });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.currentTarget.blur();
                      if (e.key === 'Escape') {
                        const saved = e.currentTarget.dataset.savedName ?? selected.name;
                        setGroups((list) =>
                          list.map((g) => (g.id === selected.id ? { ...g, name: saved } : g)),
                        );
                        e.currentTarget.blur();
                      }
                    }}
                  />
                ) : (
                  <h2>{selected.name}</h2>
                )}
                <div className="group-detail-actions">
                  {selected.canCreateBoard && (
                    <button
                      type="button"
                      className="primary-btn"
                      disabled={busy}
                      onClick={() => void createBoard()}
                    >
                      <Icon name="boards" size={14} />
                      Доска занятий
                    </button>
                  )}
                  {selected.canManage && (
                    <button
                      type="button"
                      className="ghost-btn is-danger"
                      disabled={busy}
                      onClick={() => void removeGroup()}
                    >
                      <Icon name="trash" size={14} />
                      Удалить
                    </button>
                  )}
                </div>
              </header>

              {studentMode ? (
                <StudentGroupBody group={selected} onOpenBoard={onOpenBoard} />
              ) : (
                <>
                  <section className="group-section group-section-card">
                    <h3>Доски занятий</h3>
                    {selected.boards.length === 0 ? (
                      <p className="org-members-hint">
                        Пока нет — создайте первую, она откроется у всех участников.
                      </p>
                    ) : (
                      <BoardList
                        boards={selected.boards}
                        onOpenBoard={onOpenBoard}
                        canRename={selected.canManage || selected.canCreateBoard}
                        renamingDisabled={busy}
                        onRename={(id, name) => void renameBoard(id, name)}
                      />
                    )}
                  </section>

                  {selected.canManage && (
                    <>
                      <MemberPicker
                        title="Кураторы"
                        pool={curators}
                        selectedIds={selected.curatorIds}
                        empty="Назначьте роль «Куратор» в разделе участников."
                        disabled={busy}
                        onToggle={(id) => void toggleMember('curatorIds', id)}
                      />
                      <MemberPicker
                        title="Учителя"
                        pool={teachers}
                        selectedIds={selected.teacherIds}
                        empty="В организации пока нет учителей."
                        disabled={busy}
                        onToggle={(id) => void toggleMember('teacherIds', id)}
                      />
                      <MemberPicker
                        title="Ученики"
                        pool={students}
                        selectedIds={selected.studentIds}
                        empty="В организации пока нет учеников."
                        disabled={busy}
                        onToggle={(id) => void toggleMember('studentIds', id)}
                      />
                    </>
                  )}

                  {!selected.canManage && (
                    <StaffGroupMembers members={selected.members} />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Student-only layout: three clear blocks — lesson boards, their teacher(s),
 * then classmates. Curators and the personal «Ваш учитель» framing stay off
 * staff screens; teachers never see this body.
 */
function StudentGroupBody({
  group,
  onOpenBoard,
}: {
  group: GroupView;
  onOpenBoard: (boardId: string) => void;
}) {
  const teachers = group.members.filter((m) => m.inGroupAs === 'teacher' || m.isMyTeacher);
  const classmates = group.members.filter((m) => m.inGroupAs === 'student');

  return (
    <div className="student-group-body">
      <section className="group-section group-section-card">
        <h3>Доски занятий</h3>
        {group.boards.length === 0 ? (
          <p className="org-members-hint">Пока нет досок занятий — учитель добавит их позже.</p>
        ) : (
          <BoardList boards={group.boards} onOpenBoard={onOpenBoard} />
        )}
      </section>

      <section className="group-section group-section-card">
        <h3>{teachers.length === 1 ? 'Ваш учитель' : 'Ваши учителя'}</h3>
        {teachers.length === 0 ? (
          <p className="org-members-hint">Учитель группы ещё не назначен.</p>
        ) : (
          <ul className="group-members-readonly">
            {teachers.map((m) => (
              <MemberRow
                key={m.userId}
                member={m}
                badge={m.isMyTeacher || teachers.length === 1 ? 'Ваш учитель' : 'Учитель'}
                badgeClass="is-teacher"
              />
            ))}
          </ul>
        )}
      </section>

      <section className="group-section group-section-card">
        <h3>
          Участники группы
          <em>{classmates.length}</em>
        </h3>
        {classmates.length === 0 ? (
          <p className="org-members-hint">В группе пока только вы.</p>
        ) : (
          <ul className="group-members-readonly">
            {classmates.map((m) => (
              <MemberRow key={m.userId} member={m} badge="Ученик" badgeClass="is-student" />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function BoardList({
  boards,
  onOpenBoard,
  canRename = false,
  renamingDisabled = false,
  onRename,
}: {
  boards: GroupView['boards'];
  onOpenBoard: (boardId: string) => void;
  canRename?: boolean;
  renamingDisabled?: boolean;
  onRename?: (boardId: string, name: string) => void;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);

  return (
    <ul className="group-boards">
      {boards.map((board) => (
        <li key={board.id} className="group-board-row">
          {editingId === board.id && canRename && onRename ? (
            <form
              className="group-board-rename"
              onSubmit={(e) => {
                e.preventDefault();
                const input = e.currentTarget.elements.namedItem('name') as HTMLInputElement;
                const next = input.value.trim();
                setEditingId(null);
                if (next && next !== board.name) onRename(board.id, next);
              }}
            >
              <Icon name="boards" size={14} />
              <input
                name="name"
                autoFocus
                defaultValue={board.name}
                disabled={renamingDisabled}
                aria-label="Название доски"
                onBlur={(e) => {
                  const next = e.currentTarget.value.trim();
                  setEditingId(null);
                  if (next && next !== board.name) onRename(board.id, next);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setEditingId(null);
                  }
                }}
              />
            </form>
          ) : (
            <>
              <button type="button" className="group-board-open" onClick={() => onOpenBoard(board.id)}>
                <Icon name="boards" size={14} />
                <span>{board.name}</span>
                <em>{formatDateShort(new Date(board.updatedAt).toISOString())}</em>
              </button>
              {canRename && onRename && (
                <button
                  type="button"
                  className="icon-btn group-board-rename-btn"
                  aria-label={`Переименовать «${board.name}»`}
                  data-tip="Переименовать"
                  disabled={renamingDisabled}
                  onClick={() => setEditingId(board.id)}
                >
                  <Icon name="pencil" size={13} />
                </button>
              )}
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Staff read-only roster: no «Ваш учитель» — that label is for students only. */
function StaffGroupMembers({ members }: { members: GroupMemberView[] }) {
  const teachers = members.filter((m) => m.inGroupAs === 'teacher' || m.inGroupAs === 'curator');
  const students = members.filter((m) => m.inGroupAs === 'student');

  return (
    <>
      {teachers.length > 0 && (
        <section className="group-section group-section-card">
          <h3>
            Учителя и кураторы
            <em>{teachers.length}</em>
          </h3>
          <ul className="group-members-readonly">
            {teachers.map((m) => (
              <MemberRow
                key={m.userId}
                member={m}
                badge={m.inGroupAs === 'curator' ? 'Куратор' : 'Учитель'}
                badgeClass={m.inGroupAs === 'curator' ? 'is-curator' : 'is-teacher'}
              />
            ))}
          </ul>
        </section>
      )}
      <section className="group-section group-section-card">
        <h3>
          Ученики
          <em>{students.length}</em>
        </h3>
        {students.length === 0 ? (
          <p className="org-members-hint">Учеников в группе пока нет.</p>
        ) : (
          <ul className="group-members-readonly">
            {students.map((m) => (
              <MemberRow key={m.userId} member={m} badge="Ученик" badgeClass="is-student" />
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function MemberRow({
  member,
  badge,
  badgeClass,
}: {
  member: GroupMemberView;
  badge: string;
  badgeClass: string;
}) {
  return (
    <li>
      <span className="person-avatar" style={{ background: member.color }}>
        {member.name.slice(0, 1).toUpperCase()}
      </span>
      <span className="group-member-text">
        <strong>{member.name}</strong>
        <em className={`kind-badge ${badgeClass}`}>{badge}</em>
      </span>
    </li>
  );
}

function MemberPicker({
  title,
  pool,
  selectedIds,
  empty,
  disabled,
  onToggle,
}: {
  title: string;
  pool: RosterMember[];
  selectedIds: string[];
  empty: string;
  disabled: boolean;
  onToggle: (userId: string) => void;
}) {
  const selected = new Set(selectedIds);
  return (
    <section className="group-section">
      <h3>
        {title}
        <em>{selectedIds.length}</em>
      </h3>
      {pool.length === 0 ? (
        <p className="org-members-hint">{empty}</p>
      ) : (
        <ul className="group-picker">
          {pool.map((person) => (
            <li key={person.userId}>
              <label className={selected.has(person.userId) ? 'is-on' : ''}>
                <input
                  type="checkbox"
                  checked={selected.has(person.userId)}
                  disabled={disabled}
                  onChange={() => onToggle(person.userId)}
                />
                <span className="person-avatar" style={{ background: person.color }}>
                  {person.name.slice(0, 1).toUpperCase()}
                </span>
                <span>
                  {person.name}
                  <small>{person.email}</small>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
